import React from 'react'; 
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';
import {
  Microscope,
  AlertTriangle,
  ShieldCheck,
  FileCheck,
  Settings,
  Users,
  ArrowRight
} from 'lucide-react';

const timelineSteps = [
  {
    icon: Settings,
    title: 'Node Selection',
    description: 'Break down the process into manageable sections for detailed analysis',
    image: '/nodeselection.png'
  },
  {
    icon: Microscope,
    title: 'Parameter Analysis',
    description: 'Identify process parameters and their design intentions',
    image: '/parameteranalysis.png'
  },
  {
    icon: AlertTriangle,
    title: 'Deviation Identification',
    description: 'Apply guidewords to parameters to identify potential deviations',
    image: '/deviation.jpeg'
  },
  {
    icon: ShieldCheck,
    title: 'Risk Assessment',
    description: 'Evaluate consequences and existing safeguards',
    image: '/risk.jpeg'
  },
  {
    icon: Users,
    title: 'Team Review',
    description: 'Collaborative review and recommendation development',
    image: '/team.png'
  },
  {
    icon: FileCheck,
    title: 'Documentation',
    description: 'Generate comprehensive study reports and action items',
    image: '/documentation.jpeg'
  }
];

export default function HazopTimeline() {
  const { ref, inView } = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  return (
    <section className="py-16 sm:py-20 bg-gradient-to-br from-black to-slate-900">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            HAZOP Study Process
          </h2>
          <p className="text-base sm:text-xl text-gray-300 max-w-2xl mx-auto">
            Our software guides you through each step of the HAZOP study process,
            ensuring thorough analysis and documentation.
          </p>
        </div>

        <div ref={ref} className="relative">
          <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-blue-500 opacity-20" />

          {timelineSteps.map((step, index) => {
            const Icon = step.icon;
            const isEven = index % 2 === 0;

            return (
              <div
                key={index}
                className={`flex flex-col lg:${isEven ? 'flex-row' : 'flex-row-reverse'} items-center gap-6 sm:gap-8 mb-16 sm:mb-20`}
              >
                {/* Content */}
                <div className="w-full lg:w-1/2 px-2 sm:px-4">
                  <div className="bg-white/5 backdrop-blur-lg rounded-xl p-4 sm:p-6 border border-white/10 text-center lg:text-left">
                    <div className="flex flex-col sm:flex-row items-center lg:items-start gap-3 sm:gap-4 mb-4">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                        <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400" />
                      </div>
                      <h3 className="text-xl sm:text-2xl font-semibold text-white">
                        {step.title}
                      </h3>
                    </div>
                    <p className="text-sm sm:text-base text-gray-300 mb-4">
                      {step.description}
                    </p>
                    {index < timelineSteps.length - 1 && (
                      <ArrowRight
                        className={`w-5 h-5 sm:w-6 sm:h-6 text-blue-400 mx-auto lg:ml-auto ${
                          isEven ? '' : 'rotate-180'
                        }`}
                      />
                    )}
                  </div>
                </div>

                {/* Timeline node (hidden on small screens) */}
                <div className="hidden lg:flex relative items-center justify-center">
                  <div className="w-8 h-8 bg-blue-500 rounded-full z-10 relative">
                    <div className="w-full h-full animate-ping absolute bg-blue-500 rounded-full opacity-20" />
                  </div>
                </div>

                {/* Image */}
                <div className="w-full lg:w-1/2 flex justify-center">
                  <img
                    src={step.image}
                    alt={step.title}
                    className="w-1/2 max-w-xs sm:max-w-md md:max-w-lg h-auto rounded-xl shadow-2xl"
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
